/**
 * `/selfhost-notify` — operator view of the Self-host DOI signup list
 * (OCP-200). Sits in the Customers sidebar group alongside Accounts /
 * Tenants / Portal codes / Waitlist / Invite codes (interested-parties
 * who aren't customers yet).
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { sensitiveReadUrl } from '../lib/sensitive-read';
import { fmtDate } from '../lib/format-time';

type Status = 'pending' | 'verified' | 'unsubscribed';

interface NotifyRow {
  id: string;
  email: string;
  locale: string;
  source: string | null;
  status: Status;
  created_at: string;
  verified_at: string | null;
  unsubscribed_at: string | null;
}

interface Counts {
  pending: number;
  verified: number;
  unsubscribed: number;
  total: number;
}

interface ListResponse {
  items: NotifyRow[];
  total: number;
  counts: Counts;
}

const STATUS_FILTERS: Array<{ value: 'all' | Status; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'pending', label: 'Pending verify' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];

const PAGE_SIZE = 50;

export function AdminSelfHostNotifyPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | Status>('all');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState<
    | { kind: 'unsubscribe'; row: NotifyRow }
    | { kind: 'delete'; row: NotifyRow }
    | null
  >(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (status !== 'all') params.set('status', status);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    try {
      const res = await api<ListResponse>(
        `/v1/admin/selfhost-notify?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      if (err instanceof ApiError) setError(`Load failed (HTTP ${err.status}).`);
      else setError('Load failed.');
    } finally {
      setLoading(false);
    }
  }, [search, status, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [search, status]);

  function downloadCsv(opts: { includePending?: boolean; includeUnsubscribed?: boolean } = {}) {
    const params = new URLSearchParams();
    if (opts.includePending) params.set('include_pending', 'true');
    if (opts.includeUnsubscribed) params.set('include_unsubscribed', 'true');
    // OCP-930 - every subscriber address in one file: a sensitive read with a reason.
    const url = sensitiveReadUrl(`/v1/admin/selfhost-notify/export.csv${params.toString() ? `?${params}` : ''}`, 'The subscriber export');
    if (url) window.location.href = url;
  }

  const items = data?.items ?? [];
  const counts = data?.counts ?? { pending: 0, verified: 0, unsubscribed: 0, total: 0 };
  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Self-host notify list</h1>
        <p className="mt-1 text-xs text-gray-500">
          DOI signups for the Self-host availability announcement. Verified rows
          are the ones safe to email when Self-host launches.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <CountPill label="verified" value={counts.verified} tone="green" />
        <CountPill label="pending verify" value={counts.pending} tone="amber" />
        <CountPill label="unsubscribed" value={counts.unsubscribed} tone="neutral" />
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={() => downloadCsv()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Download verified CSV
          </button>
          <button
            type="button"
            onClick={() => downloadCsv({ includePending: true, includeUnsubscribed: true })}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
            title="Includes pending + unsubscribed too. Use for compliance audits."
          >
            Download all
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email…"
          className="flex-1 min-w-[250px] rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'all' | Status)}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <div className="text-xs text-gray-500">
          {loading ? 'Loading…' : `${total} matching`}
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          {counts.total === 0
            ? 'No Self-host notify signups yet.'
            : 'No rows match the current filter.'}
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Locale</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Signed up</th>
                <th className="px-3 py-2">Verified</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((r) => (
                <tr key={r.id} className={r.status === 'unsubscribed' ? 'opacity-60' : ''}>
                  <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.locale}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {r.source ?? <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {r.verified_at
                      ? fmtDate(r.verified_at)
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-3">
                      {r.status !== 'unsubscribed' && (
                        <button
                          type="button"
                          onClick={() => setConfirm({ kind: 'unsubscribe', row: r })}
                          className="text-xs text-amber-700 dark:text-amber-300 hover:underline"
                        >
                          Unsubscribe
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConfirm({ kind: 'delete', row: r })}
                        className="text-xs text-red-600 hover:underline"
                        title="GDPR data-deletion. Hard-removes the row."
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            ← Prev
          </button>
          <div className="text-xs text-gray-500">
            {offset + 1}–{Math.min(offset + items.length, total)} of {total}
          </div>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            Next →
          </button>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          confirm={confirm}
          onCancel={() => setConfirm(null)}
          onDone={async () => {
            setConfirm(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function CountPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'amber' | 'neutral';
}) {
  const cls =
    tone === 'green'
      ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
      : tone === 'amber'
        ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
        : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
  return (
    <span className={`rounded border px-2.5 py-1 text-xs ${cls}`}>
      <span className="tabular-nums font-medium">{value}</span> {label}
    </span>
  );
}

function StatusPill({ status }: { status: Status }) {
  const cfg = {
    verified: { label: 'verified', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    pending: { label: 'pending', cls: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' },
    unsubscribed: { label: 'unsubscribed', cls: 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
  }[status];
  return (
    <span className={`rounded border px-2 py-0.5 text-xs ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function ConfirmModal({
  confirm,
  onCancel,
  onDone,
}: {
  confirm:
    | { kind: 'unsubscribe'; row: NotifyRow }
    | { kind: 'delete'; row: NotifyRow };
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = typed.trim() === confirm.row.email;

  const cfg =
    confirm.kind === 'unsubscribe'
      ? {
          title: 'Force-unsubscribe',
          body:
            'Stamps unsubscribed_at on this row. The signup stays in the DB (for analytics + audit), but never receives future blasts. Reversible only by re-signup from the customer side.',
          cta: 'Unsubscribe',
          method: 'POST' as const,
          url: `/v1/admin/selfhost-notify/${confirm.row.id}/unsubscribe`,
          danger: false,
        }
      : {
          title: 'Hard-delete (GDPR)',
          body:
            'Removes the row entirely. Use for GDPR data-deletion requests only. Audit log keeps your operator email + the deleted address for compliance proof, but the row is gone.',
          cta: 'Delete',
          method: 'DELETE' as const,
          url: `/v1/admin/selfhost-notify/${confirm.row.id}`,
          danger: true,
        };

  async function submit() {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(cfg.url, { method: cfg.method });
      await onDone();
    } catch (err) {
      if (err instanceof ApiError) setError(`HTTP ${err.status}`);
      else setError('Failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">{cfg.title}</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">{cfg.body}</p>
        <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
          Type <code className="font-mono text-xs">{confirm.row.email}</code> to confirm:
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={confirm.row.email}
          className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
        />
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || submitting}
            onClick={() => void submit()}
            className={`rounded px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50 ${
              cfg.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {submitting ? 'Submitting…' : cfg.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
